import type { AuditPolicyData, AuditOwnershipChange } from '@/hooks/useAuditData';
import { format } from 'date-fns';

// Map database status to display status
function getDisplayStatus(status: string): string {
  if (status === 'Approved' || status === 'Locked') {
    return 'Approved';
  }
  if (status === 'draft') {
    return 'Draft';
  }
  if (status === 'pending_approval') {
    return 'Pending Approval';
  }
  if (status === 'expired') {
    return 'Archived';
  }
  return status;
}

export function exportAuditCsv(
  policies: AuditPolicyData[],
  ownershipChanges: AuditOwnershipChange[],
  clinicName: string,
  generatedBy: string
) {
  // Filter out draft policies from audit export
  const auditPolicies = policies.filter(p => p.status !== 'draft');
  
  const rows: string[][] = [];

  // Header metadata
  rows.push(['Compliance Audit Report']);
  rows.push([`Clinic: ${clinicName}`]);
  rows.push([`Generated: ${format(new Date(), 'PPpp')}`]);
  rows.push([`Generated By: ${generatedBy}`]);
  rows.push([]);

  // Policy Acknowledgements Section
  rows.push(['POLICY ACKNOWLEDGEMENTS']);
  rows.push([
    'Policy Name',
    'Category',
    'Status',
    'Effective Date',
    'Next Review',
    'Requires Ack',
    'Assigned To',
    'Version',
    'Version Created',
    'Created By',
    'Approved By',
    'Acknowledged By',
    'Acknowledged At',
  ]);

  auditPolicies.forEach(policy => {
    if (policy.versions.length === 0) {
      // Policy with no versions
      rows.push([
        policy.name,
        policy.category,
        getDisplayStatus(policy.status),
        policy.effective_date,
        policy.next_review_date,
        policy.requires_acknowledgement ? 'Yes' : 'No',
        policy.owner_name,
        '',
        '',
        '',
        '',
        '',
        '',
      ]);
    } else {
      policy.versions.forEach(version => {
        if (version.acknowledgements.length === 0) {
          // Version with no acknowledgements
          rows.push([
            policy.name,
            policy.category,
            getDisplayStatus(policy.status),
            policy.effective_date,
            policy.next_review_date,
            policy.requires_acknowledgement ? 'Yes' : 'No',
            policy.owner_name,
            `v${version.version_number}`,
            format(new Date(version.created_at), 'PP'),
            version.created_by_name,
            version.approved_by_name ?? '',
            'No acknowledgements',
            '',
          ]);
        } else {
          version.acknowledgements.forEach(ack => {
            rows.push([
              policy.name,
              policy.category,
              getDisplayStatus(policy.status),
              policy.effective_date,
              policy.next_review_date,
              policy.requires_acknowledgement ? 'Yes' : 'No',
              policy.owner_name,
              `v${version.version_number}`,
              format(new Date(version.created_at), 'PP'),
              version.created_by_name,
              version.approved_by_name ?? '',
              ack.acknowledged_by_name,
              format(new Date(ack.acknowledged_at), 'PPpp'),
            ]);
          });
        }
      });
    }
  });

  rows.push([]);

  // Ownership History Section
  rows.push(['OWNERSHIP HISTORY']);
  rows.push([
    'Policy Name',
    'Previous Assignee',
    'New Assignee',
    'Changed By',
    'Changed At',
  ]);

  if (ownershipChanges.length === 0) {
    rows.push(['No ownership changes recorded']);
  } else {
    ownershipChanges.forEach(change => {
      rows.push([
        change.policy_name,
        change.previous_owner_name ?? 'N/A (Initial)',
        change.new_owner_name,
        change.changed_by_name,
        format(new Date(change.changed_at), 'PPpp'),
      ]);
    });
  }

  // Convert to CSV string
  const csvContent = rows
    .map(row =>
      row
        .map(cell => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const escaped = String(cell).replace(/"/g, '""');
          if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
            return `"${escaped}"`;
          }
          return escaped;
        })
        .join(',')
    )
    .join('\n');

  // Download file with clinic name in filename
  const safeClinicName = clinicName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${safeClinicName}_audit-export_${format(new Date(), 'yyyy-MM-dd')}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}
